/**
 * PremiumTriggerSheet — Bottom sheet (mobile) / right-side sheet (desktop)
 * that intercepts a Free instructor when they try to access a Pro-gated feature.
 *
 * Used by Trigger B (Roll Review block) and reusable for any future gate.
 *
 * Layout matches Spec SOS-332 rule 2:
 *   - Icon + title ("Roll Review is Pro")
 *   - 1-2 line subtitle
 *   - ROI callout
 *   - 3 benefits list
 *   - Primary CTA "Subscribe to Pro · $X/month" → paywall
 *   - Secondary CTA "Not now" → close
 *
 * State-aware:
 *   - For `expired` subscriptions: title becomes "Reactivate Pro" + CTA goes to paywall.
 *   - For `active`/`past_due`/`canceled_active`: sheet does not render
 *     (parent component should not even instantiate it — guard via useFeatureGate).
 *
 * Parameterized: copy keys come from i18n; price comes from usePremiumPrice;
 * benefits use the same feature registry as the paywall.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { usePremiumPrice } from '../hooks/usePremiumPrice';
import {
  usePremiumTriggerAnalytics,
  type PremiumTriggerOrigin,
} from '../hooks/usePremiumTriggerAnalytics';

interface PremiumTriggerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Telemetry origin label — propagated to analytics events. */
  origin: PremiumTriggerOrigin;
  /** Override title/subtitle. Defaults to the Roll Review block copy. */
  titleKey?: string;
  subtitleKey?: string;
  /** Pass `true` if the subscription is expired (changes the primary CTA copy). */
  isReactivation?: boolean;
}

const DEFAULT_BENEFIT_KEYS = [
  'premium.benefits.roll_review',
  'premium.benefits.badge',
  'premium.benefits.boost',
];

export const PremiumTriggerSheet: React.FC<PremiumTriggerSheetProps> = ({
  open,
  onOpenChange,
  origin,
  titleKey,
  subtitleKey,
  isReactivation = false,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { displayAmount } = usePremiumPrice();
  const { trackShown, trackClicked, trackDismissed } = usePremiumTriggerAnalytics(origin);

  // Distinguish "user closed without converting" from "user clicked Subscribe
  // and the sheet auto-closed". `clickedSubscribe` is set true only on the
  // happy path, then read on the close transition. `wasOpen` skips the first
  // render (open is false on mount) so we never fire dismiss without a prior
  // shown event.
  const clickedSubscribeRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      clickedSubscribeRef.current = false;
      trackShown();
    } else if (wasOpenRef.current && !clickedSubscribeRef.current) {
      trackDismissed();
    }
  }, [open, trackShown, trackDismissed]);

  const onSubscribe = () => {
    clickedSubscribeRef.current = true;
    trackClicked();
    onOpenChange(false);
    navigate('/subscription', { state: { origin } });
  };

  const title = t(titleKey ?? (isReactivation ? 'premium.trigger.reactivate_title' : 'premium.trigger.roll_review_title'));
  const subtitle = t(subtitleKey ?? 'premium.trigger.roll_review_subtitle');
  const ctaLabel = isReactivation
    ? t('premium.trigger.reactivate_cta')
    : t('premium.trigger.subscribe_cta', { price: displayAmount ?? '' });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="premium-trigger-sheet"
        side="bottom"
        className="bg-[#160C0C] border-t border-white/10 text-white rounded-t-2xl px-5 pt-6 pb-8 max-h-[92vh] overflow-y-auto"
      >
        <SheetHeader className="text-left space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-[#D72E16]/15 flex items-center justify-center">
            <Sparkles size={26} className="text-[#D72E16]" />
          </div>
          <SheetTitle className="text-white text-xl font-bold leading-tight">
            {title}
          </SheetTitle>
          <SheetDescription className="text-white/60 text-sm leading-relaxed">
            {subtitle}
          </SheetDescription>
        </SheetHeader>

        {/* ROI callout — reuses paywall copy */}
        <div className="mt-4 rounded-xl border border-[#D72E16]/30 bg-[#D72E16]/10 p-3 text-xs text-white/90 leading-relaxed">
          {t('premium.roi_callout')}
        </div>

        {/* Benefits */}
        <ul className="mt-4 space-y-2">
          {DEFAULT_BENEFIT_KEYS.map((k) => (
            <li key={k} className="flex items-center gap-2 text-sm text-white/90">
              <Check size={14} className="text-emerald-400 flex-shrink-0" />
              {t(k)}
            </li>
          ))}
        </ul>

        {/* CTAs */}
        <div className="mt-6 flex flex-col gap-2">
          <Button variant="primary" onClick={onSubscribe} className="w-full">
            {ctaLabel}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full text-white/70"
          >
            {t('premium.trigger.not_now')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
